/**
 * AGRI-OPERATIONAL-SECTOR-R2 — shared valve command-safety primitives.
 *
 * These helpers were EXTRACTED VERBATIM from the proven IoTNodes pilotage
 * command machine so that a second surface (the operational Sector Detail) can
 * reuse the exact same discipline instead of re-implementing a simplified — and
 * therefore less safe — variant. IoTNodes now imports them; its behaviour is
 * unchanged.
 *
 * Everything here is PURE: no React, no network, no side effects (the only
 * exception being the idempotency-key counter, which is a deliberate
 * process-local monotonic sequence).
 *
 * Safety invariants encoded here:
 *   - A command status is "busy" until the backend proves a terminal outcome.
 *   - An unknown / missing / malformed status never re-enables a control.
 *   - A valve target's current state is derived ONLY from an exact canonical
 *     measurement (or the exact scalar field) — never inferred, never toggled
 *     blind.
 *   - A post-success unlock requires PHYSICAL proof from a refreshed snapshot,
 *     not the HTTP success of the POST.
 */

import type { CanonicalMeasurement, IoTNode } from './iotApi';

/** Exact internal ASVWF01 valve channels, in farmer-friendly order. */
export const ASVWF01_CHANNELS: ReadonlyArray<{ id: string; n: number }> = [
  { id: 'valve_1', n: 1 },
  { id: 'valve_2', n: 2 },
  { id: 'valve_3', n: 3 },
  { id: 'valve_4', n: 4 },
];

/** Models that legitimately expose an actionable valve. */
export const VALVE_STATE_MODELS: ReadonlySet<string> = new Set([
  'asvwf01',
  'valve_water_flow',
]);

/**
 * A target with one of these statuses must remain disabled (fail-closed).
 * REQUESTED/SENT_TO_TTN are backend non-terminal states; UNCERTAIN and
 * REFRESHING are local fail-closed states.
 */
export const BUSY_COMMAND_STATUSES: ReadonlySet<string> = new Set([
  'REQUESTED',
  'SENT_TO_TTN',
  'UNCERTAIN',
  'REFRESHING',
]);

/** Closed (0) → open; open (1) → close. Never a blind toggle. */
export const ACTION_BY_STATE: Record<0 | 1, 'on' | 'off'> = { 0: 'on', 1: 'off' };

export type ValveTarget =
  | { kind: 'scalar'; nodeId: string }
  | { kind: 'channel'; nodeId: string; channelId: string };

/**
 * Per-target proof that a post-success node refresh reflects the successful
 * physical outcome. A locally-issued intent is proven by the refreshed target
 * state matching the desired state (closed→open / open→close); an external or
 * adopted command has no locally-known desired state, so it is proven by the
 * refreshed node timestamp being at/after the successful observation.
 */
export type RefreshProof =
  | { kind: 'expectedState'; nodeId: string; channelId: string | null; value: 0 | 1; afterTimestamp?: number }
  | { kind: 'observationTimestamp'; nodeId: string; channelId: string | null; ts: number };

export const scalarTargetKey = (nodeId: string): string => `scalar:${nodeId}`;

export const channelTargetKey = (nodeId: string, channelId: string): string =>
  `channel:${nodeId}:${channelId}`;

export const targetKey = (t: ValveTarget): string =>
  t.kind === 'scalar' ? scalarTargetKey(t.nodeId) : channelTargetKey(t.nodeId, t.channelId);

/** A raw status is "busy" when the target must stay disabled. */
export const isBusyStatus = (raw: string | null | undefined): boolean =>
  !!raw && BUSY_COMMAND_STATUSES.has(raw);

/** A raw backend status is terminal (no longer pending). */
export const isTerminalCommandStatus = (s: string): boolean =>
  s === 'OBSERVED_SUCCESS' || s === 'OBSERVED_FAILED' || s === 'TIMEOUT';

/** Map a raw backend/local command status to a farmer-safe presentation kind. */
export const commandStatusKind = (
  raw: string | null | undefined,
): 'pending' | 'confirmed' | 'failed' | 'timeout' | 'uncertain' | null => {
  if (!raw) return null;
  if (raw === 'REQUESTED' || raw === 'SENT_TO_TTN') return 'pending';
  if (raw === 'UNCERTAIN') return 'uncertain';
  if (raw === 'REFRESHING' || raw === 'OBSERVED_SUCCESS') return 'confirmed';
  if (raw === 'OBSERVED_FAILED') return 'failed';
  if (raw === 'TIMEOUT') return 'timeout';
  return null;
};

/** Parse a timestamp string to epoch ms; absent/invalid → null. */
export const parseTimestampMs = (ts: string | null | undefined): number | null => {
  if (typeof ts !== 'string' || ts.length === 0) return null;
  const ms = Date.parse(ts);
  return Number.isFinite(ms) ? ms : null;
};

/** Freshest valid communication timestamp across a node's fields (epoch ms), or null. */
export const nodeTimestampMs = (node: IoTNode): number | null => {
  const candidates = [node.last_seen_at, node.latest?.ts]
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .map((value) => Date.parse(value))
    .filter((ms) => Number.isFinite(ms));
  return candidates.length > 0 ? Math.max(...candidates) : null;
};

/**
 * Resolve the freshest explicit valve state between the root node.valve_state
 * and latest.measures.valve_state. Both must be exact 0/1. When both are
 * present, freshness is decided by last_seen_at vs latest.ts; missing/invalid
 * timestamps make the choice ambiguous → null (never invent a state).
 */
export const resolveFreshestValve = (
  node: IoTNode,
  rootValve: number | null,
  latestValve: number | null,
): number | null => {
  if (rootValve === null && latestValve === null) return null;
  if (rootValve === null) return latestValve;
  if (latestValve === null) return rootValve;

  const lastSeenMs = parseTimestampMs(node.last_seen_at);
  const latestTsMs = parseTimestampMs(node.latest?.ts);
  if (lastSeenMs !== null && latestTsMs !== null) {
    return lastSeenMs >= latestTsMs ? rootValve : latestValve;
  }
  return null;
};

/**
 * Scalar valve state (valve-capable models only), resolving root-vs-latest
 * freshness. Non valve-capable models, or an ambiguous/absent state → null.
 */
export const resolveScalarValveState = (node: IoTNode): 0 | 1 | null => {
  if (!VALVE_STATE_MODELS.has(node.model)) return null;
  const measures = node.latest?.measures;
  const rootValve =
    node.valve_state === 0 || node.valve_state === 1 ? node.valve_state : null;
  const latestValve =
    measures && (measures.valve_state === 0 || measures.valve_state === 1)
      ? measures.valve_state
      : null;
  const fresh = resolveFreshestValve(node, rootValve, latestValve);
  return fresh === 0 || fresh === 1 ? fresh : null;
};

/**
 * ASVWF01 per-channel valve states, derived ONLY from canonical measurements
 * present in latest.measures.canonical_measurements (measurement_id ==
 * "valve_state", exact channel_id, exact value 0/1). Anything else → unknown.
 */
export const deriveChannelValveStates = (node: IoTNode): Map<string, 0 | 1> => {
  const out = new Map<string, 0 | 1>();
  const list: CanonicalMeasurement[] | undefined =
    node.latest?.measures?.canonical_measurements;
  if (!Array.isArray(list)) return out;
  for (const m of list) {
    if (m.measurement_id !== 'valve_state') continue;
    if (typeof m.channel_id !== 'string') continue;
    if (m.value !== 0 && m.value !== 1) continue;
    out.set(m.channel_id, m.value as 0 | 1);
  }
  return out;
};

export interface ValveTargetView {
  kind: 'scalar' | 'channel';
  state: 0 | 1 | null;
  channelId?: string;
  channelN?: number;
}

/**
 * Enumerate the actionable valve targets for a node.
 *  - valve_water_flow → single scalar valve (freshest root-vs-latest state).
 *  - asvwf01          → four channels (canonical per-channel state).
 *  - everything else  → no targets (relay/soil/pressure/unknown stay read-only).
 */
export const valveTargetsFor = (node: IoTNode): ValveTargetView[] => {
  if (node.model === 'valve_water_flow') {
    const state = resolveScalarValveState(node);
    return state === null ? [] : [{ kind: 'scalar', state }];
  }
  if (node.model === 'asvwf01') {
    const states = deriveChannelValveStates(node);
    return ASVWF01_CHANNELS.map((ch) => ({
      kind: 'channel' as const,
      channelId: ch.id,
      channelN: ch.n,
      state: states.get(ch.id) ?? null,
    }));
  }
  return [];
};

/** Current 0/1 state of a valve target from a node snapshot, or null if unknown. */
export const targetStateFromNode = (node: IoTNode, channelId: string | null): 0 | 1 | null => {
  const targets = valveTargetsFor(node);
  if (channelId === null) {
    const scalar = targets.find((t) => t.kind === 'scalar');
    return scalar ? scalar.state : null;
  }
  const channel = targets.find((t) => t.kind === 'channel' && t.channelId === channelId);
  return channel ? channel.state : null;
};

/**
 * Timestamp (epoch ms) of the EXACT target valve-state snapshot, or null.
 * A timestamp is returned ONLY when the same snapshot (node.latest) contains a
 * valid 0/1 state for the EXACT requested target. For a scalar valve that is
 * latest.measures.valve_state; for an ASVWF01 channel it is the exact canonical
 * measurement (measurement_id == "valve_state", channel_id == target). Only
 * node.latest.ts is authoritative for the snapshot it carries — a newer
 * node.last_seen_at never proves the target snapshot is post-command.
 */
export const targetSnapshotTimestampMs = (
  node: IoTNode,
  channelId: string | null,
): number | null => {
  const latest = node.latest;
  const ts = parseTimestampMs(latest?.ts);
  if (ts === null) return null;
  const measures = latest?.measures;
  if (!measures) return null;
  if (channelId === null) {
    // Scalar valve: exact latest.measures.valve_state must be valid 0/1.
    if (measures.valve_state !== 0 && measures.valve_state !== 1) return null;
    return ts;
  }
  // ASVWF01 channel: exact canonical valve_state measurement for the channel.
  const list = measures.canonical_measurements;
  if (!Array.isArray(list)) return null;
  for (const m of list) {
    if (m.measurement_id !== 'valve_state') continue;
    if (m.channel_id !== channelId) continue;
    if (m.value !== 0 && m.value !== 1) continue;
    return ts;
  }
  return null;
};

/**
 * Verify a RefreshProof against a refreshed node list. Returns false whenever
 * the proof cannot be positively established — an absent node, a stale/unknown
 * snapshot, or a mismatching state all keep the target locked (fail-closed).
 */
export const isRefreshProven = (proof: RefreshProof, node: IoTNode | undefined): boolean => {
  if (!node) return false;
  if (proof.kind === 'expectedState') {
    if (targetStateFromNode(node, proof.channelId) !== proof.value) return false;
    if (proof.afterTimestamp !== undefined) {
      const ts = targetSnapshotTimestampMs(node, proof.channelId);
      return ts !== null && ts > proof.afterTimestamp;
    }
    return true;
  }
  const targetTs = targetSnapshotTimestampMs(node, proof.channelId);
  return targetTs !== null && targetTs >= proof.ts;
};

/**
 * The POST path for a valve target. Scalar valves use the legacy scalar route;
 * ASVWF01 channels use the exact per-channel route. No other route may ever
 * issue a physical valve command.
 */
export const valveCommandPath = (target: ValveTarget): string =>
  target.kind === 'scalar'
    ? `/iot/nodes/${target.nodeId}/valve`
    : `/iot/nodes/${target.nodeId}/valves/${target.channelId}`;

// One stable unique key per confirmed user intent. Never reused across intents;
// never regenerated for the same intent (no automatic retry).
let idempotencySeq = 0;
export const generateIdempotencyKey = (prefix = 'valve'): string => {
  idempotencySeq += 1;
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix}-${Date.now().toString(36)}-${idempotencySeq.toString(36)}-${rand}`;
};
